import type Konva from "konva";
import { ShapeType, type Shape } from "~/telestrations/types/canvas";

interface ShapeAttrs {
  x: number;
  y: number;
  width?: number;
  height?: number;
  radiusX?: number;
  radiusY?: number;
  scaleX?: number;
  scaleY?: number;
  rotation: number;
  id?: string;
  name?: string;
};

export function rotatePoint(point: { x: number; y: number }, angle: number): { x: number; y: number } {
  const rad = angle * Math.PI / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return {
    x: point.x * cos - point.y * sin,
    y: point.x * sin + point.y * cos,
  };
};

function drawRect(
  context: Konva.Context,
  x: number, y: number,
  width: number,
  height: number,
  rotation: number,
): void {
  // Apply transformations to the inner rectangle
  const points = [
    { x: 0, y: height }, // bottom left
    { x: width, y: height }, // bottom right
    { x: width, y: 0 }, // top right
    { x: 0, y: 0 }, // top left
  ];
  const firstPoint = rotatePoint(points[0], rotation);
  context.moveTo(firstPoint.x + x, firstPoint.y + y);
  for (let i = 1; i < points.length; i++) {
    const rotatedPoint = rotatePoint(points[i], rotation);
    context.lineTo(rotatedPoint.x + x, rotatedPoint.y + y);
  }
}

export function mapShapeTransform(shape: Shape, attrs?: ShapeAttrs): ShapeAttrs {
  const isTransformerData = shape.id === attrs?.id;
  const rectParams = isTransformerData ? attrs : shape;
  const startX = rectParams.x ?? 0;
  const startY = rectParams.y ?? 0;
  const rotation = rectParams.rotation ?? 0;
  const scaleX = rectParams.scaleX ?? 1;
  const scaleY = rectParams.scaleY ?? 1;

  if (shape.name === SpotlightShapeName.ELLIPSE) {
    return {
      x: startX,
      y: startY,
      radiusX: (rectParams.radiusX ?? 1) * scaleX,
      radiusY: (rectParams.radiusY ?? 1) * scaleY,
      rotation,
      name: shape.name,
    };
  }

  const rectWidth = rectParams.width ?? 1;
  const rectHeight = rectParams.height ?? 1;

  const transformedEndX = startX + rectWidth * scaleX;
  const transformedEndY = startY + rectHeight * scaleY;

  const rectX = Math.min(startX, transformedEndX);
  const rectY = Math.min(startY, transformedEndY);
  const rectWidthAbs = Math.abs(transformedEndX - startX);
  const rectHeightAbs = Math.abs(transformedEndY - startY);
  return {
    x: rectX,
    y: rectY,
    width: rectWidthAbs,
    height: rectHeightAbs,
    rotation,
    name: shape.name,
  };
}

export function setupSpotLightShapeConfig(
  stage: Konva.Stage | null,
  shapes: Shape[],
  attrs?: ShapeAttrs,
): Konva.ShapeConfig {
  const spotLightShapeBase: Konva.ShapeConfig = {
    x: 0,
    y: 0,
    fill: "rgba(0, 0, 0, 0.5)",
    tension: 0.5,
    strokeWidth: 0,
    lineCap: "round",
    lineJoin: "round",
    draggable: false,
    listenToEvents: false,
    listening: false,
    moveToTop: true,
    zIndex: 3,
  };
  if (!stage || !shapes.length || !shapes.some(s => s.type === ShapeType.Spotlight)) {
    return spotLightShapeBase;
  }

  const rects = shapes.filter(s => s.type === ShapeType.Spotlight).map((s) => {
    return mapShapeTransform(s, attrs);
  });

  return {
    sceneFunc: (context: Konva.Context) => {
      context.save();
      context.beginPath();
      // Outer rectangle (full width of the stage)
      context.rect(0, 0, stage.width(), stage.height());
      // Inner rectangles (the holes in the shape)
      rects.forEach((rect) => {
        const { x, y, width, height, radiusX, radiusY, name, rotation } = rect;
        if (name === SpotlightShapeName.ELLIPSE) {
          context.ellipse(
            x,
            y,
            radiusX ?? 0,
            radiusY ?? 0,
            0,
            0,
            Math.PI * 2,
          );
        }
        else {
          drawRect(context, x, y, width ?? 0, height ?? 0, rotation);
        }
        context.closePath();
      });
      context.fillStyle = "rgba(0, 0, 0, 0.5)";
      context.fill("evenodd");
      context.restore();
    },
    ...spotLightShapeBase,
  };
}
